/**
 * Created by Ning Jiang on 2021-06-23.
 */

import {gsap, TimelineLite} from "gsap";
import {TombstoneRip} from "../../TombstoneRip";
import {TombstoneRipGameConfig} from "../../TombstoneRipGameConfig";
import {IBetWayWinData} from "../../server/data/ITombstoneRipData";
import {IndividualWinView} from "./IndividualWinView";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";

export class IndividualWinController {

    private _view:IndividualWinView;
    private _countUp:PIXI.Container;
    private _currentTimeline:TimelineLite | null;

    private _stopped:boolean;
    private _winCap:boolean;
    private _allWinPositions:boolean[][];

    constructor() {
        this._view = new IndividualWinView();
    }

    public start():void {
        this._stopped = false;
        const data = TombstoneRip.model.parsedGameData;
        this._countUp = data.currentWinRatioConfig!.bigWinLevel > -1 ? TombstoneRip.bigWinCountUp : TombstoneRip.initialCountUp;
        this._winCap = data.brokeTheBank;
        this.getAllWinPositions();
        this._view.start();
        if(data.betWins.length > 0 && data.singleWin > 0 && !TombstoneRip.isActionSpinsActive) {
            // Add a small delay for the first time, so we don't start to play before the autoPlay start next spin.
            this.playNextBetWin(0, 1);
        }
    }

    private getAllWinPositions():void {
        this._allWinPositions = ArrayHelper.initArrayWithValues<boolean[]>(TombstoneRipGameConfig.REELS_NUM, reelId => {
            return ArrayHelper.initArrayWithValues<boolean>(3, () => false)
        });
        (<IBetWayWinData[]>TombstoneRip.model.parsedGameData.betWins).forEach(w => {
            w.reelWinPositions.forEach((reelWin, reelId) => {
                reelWin.forEach((symWin, symId) => {
                    if(symWin) {
                        this._allWinPositions[reelId][symId] = true;
                    }
                });
            });
        });
    }

    private playNextBetWin(index:number, delay:number = 0):void {
        if(this._stopped) {
            return;
        }

        const data = TombstoneRip.model.parsedGameData;
        const winRatio = data.currentWinRatioConfig?.ratio!;

        let nextDelay = 0.8;
        const tl = new TimelineLite();
        if(index === data.betWins.length) {
            tl.add(this.getAllWinAnimation(winRatio), delay);
            nextDelay = 1.2;
        } else {
            if(index >= data.betWins.length) {
                index = 0;
            }
            if((index === 0 && data.betWins.length === 1) || this._winCap) {
                // do not play single if only one line.
            } else {
                tl.add(this.getBetWayAnimation(<IBetWayWinData>data.betWins[index], winRatio), delay);
            }
        }
        tl.add(() => {
            if(!this._stopped) {
                this.playNextBetWin(index + 1, nextDelay);
            }
        });
        this._currentTimeline = tl;
    }

    private getBetWayAnimation(winData:IBetWayWinData, winRatio:number):TimelineLite {
        const tl = new TimelineLite();
        tl.add([
            gsap.to(this._countUp, {externalAlpha : 0, duration : 0.2}),
            gsap.to(TombstoneRip.waysWon, {externalAlpha : 0, duration : 0.2})
        ], 0);
        const winTextPosition = this.getWinTextPosition(winData.reelWinPositions);
        winData.reelWinPositions.forEach((reelWin, reelId) => {
            reelWin.forEach((symWin, symId) => {
                if(symWin) {
                    tl.add(this.getSymbolWinAnimation(reelId, symId, winRatio, reelId === winTextPosition.x && symId === winTextPosition.y, winData), 0);
                }
            });
        });
        return tl;
    }

    private getWinTextPosition(reelWinPositions:boolean[][]):PIXI.Point {
        for(let reelId = reelWinPositions.length - 1; reelId >= 0; reelId--) {
            if(TombstoneRip.model.parsedGameData.doReelNudge[reelId]) {
                continue;
            }
            for(let symId = 0; symId < 3; symId++) {
                if(reelWinPositions[reelId][symId] && !TombstoneRip.model.parsedGameData.reels[reelId][symId+1].includes("W") && !TombstoneRip.model.parsedGameData.reels[reelId][symId+1].includes("S")) {
                    return new PIXI.Point(reelId, symId);
                }
            }
        }
        return new PIXI.Point(0, reelWinPositions[0].indexOf(true));
    }

    private getSymbolWinAnimation(reelId:number, symId:number, winRatio:number, showWinText:boolean, winData?:IBetWayWinData):TimelineLite {
        const tl = new TimelineLite();
        const sym = TombstoneRip.reels[reelId].getSymbol(symId, false, false);
        tl.add(() => {
            if(!sym.destroyed) { // Sometimes initial win timeline is still going when symbol started to spin away.
                TombstoneRip.reels[reelId].reelView.addWinSymbol(sym);
            }
        });// no need to have win state in this game?

        const position = sym.getStagePosition();
        tl.add(this._view.playAnimation(position, this.getEffectLevel(winRatio), reelId === 4, showWinText, winData));

        return tl;
    }

    private getAllWinAnimation(winRatio:number):TimelineLite {
        const tl = new TimelineLite()
        tl.add([
            gsap.to(this._countUp, {externalAlpha : 1, duration : 0.2}),
            gsap.to(TombstoneRip.waysWon, {externalAlpha : 1, duration : 0.2}),
            () => {this._view.removeWinText()}
        ], 0);
        this._allWinPositions.forEach((reelWin, reelId) => {
            reelWin.forEach((symWin, symId) => {
                if(symWin) {
                    tl.add(this.getSymbolWinAnimation(reelId, symId, winRatio, false), 0);
                }
            });
        });
        return tl;
    }

    private getEffectLevel(winRatio:number):number {
        for(let i = 1; i < 4; i++) {
            if(winRatio < TombstoneRipGameConfig.WIN_RATIOS[i].ratio) {
                return i - 1;
            }
        }
        return 3;
    }

    public async stop():Promise<any> {
        this._stopped = true;
        if(this._currentTimeline && this._currentTimeline.isActive()) {
            await this._currentTimeline.progress(1);
            this._currentTimeline.kill();
        }
        this._currentTimeline = null;
        this._view.stop();
        await Promise.resolve();
    }
}